import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader } from "@/components/Loader";
import {
    Inbox, Sparkles, Target, Users, Mail, TrendingUp, CheckCircle2, ExternalLink, FileText, ArrowRight, Eye, Zap, Package,
} from "lucide-react";

// Icon map mirrors REQUEST_TYPES in Requests.jsx so each delivery shows the right glyph.
const TYPE_META = {
    research: { label: "Firm research", icon: Sparkles, accent: "text-amber-400 border-amber-500/40 bg-amber-500/10" },
    synergy: { label: "Synergy analysis", icon: Target, accent: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10" },
    stretch: { label: "Stretch partners", icon: TrendingUp, accent: "text-blue-400 border-blue-500/40 bg-blue-500/10" },
    stakeholders: { label: "Stakeholder map", icon: Users, accent: "text-purple-400 border-purple-500/40 bg-purple-500/10" },
    outreach: { label: "Outreach email", icon: Mail, accent: "text-pink-400 border-pink-500/40 bg-pink-500/10" },
};

const fmtAgo = (iso) => {
    if (!iso) return "—";
    const ts = new Date(iso).getTime();
    if (isNaN(ts)) return "—";
    const mins = Math.max(1, Math.floor((Date.now() - ts) / 60000));
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    return new Date(iso).toLocaleDateString();
};

// LocalStorage-based "seen" tracking. Stored per-user so a shared device doesn't leak state.
const seenKey = (userId) => `affynix.deliveries.seen.${userId || "anon"}`;
export const loadSeenIds = (userId) => {
    try { return new Set(JSON.parse(localStorage.getItem(seenKey(userId)) || "[]")); }
    catch { return new Set(); }
};
export const saveSeenIds = (userId, set) => {
    try { localStorage.setItem(seenKey(userId), JSON.stringify([...set])); }
    catch { /* quota / private mode */ }
};

/**
 * Client-facing "Deliveries" page — shows ONLY completed requests with their result_link
 * + result_notes, in a clean inbox-style feed. Admins see the same view for their own
 * requests (their inbox lives at /app/requests).
 *
 * On mount, all visible deliveries are marked as "seen" so the sidebar unread badge resets.
 */
export default function Deliveries() {
    const { user } = useAuth();
    const [rows, setRows] = useState(null);
    const [err, setErr] = useState("");

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/requests?status=completed");
                if (cancelled) return;
                const list = data.requests || [];
                setRows(list);
                // Mark every visible delivery as seen — resets the unread counter.
                const seen = loadSeenIds(user?.id);
                list.forEach((r) => seen.add(r.id));
                saveSeenIds(user?.id, seen);
                // Tell the rest of the app the badge needs to refresh.
                window.dispatchEvent(new Event("affynix:deliveries-seen"));
            } catch (e) {
                if (cancelled) return;
                setErr(e?.response?.data?.detail || "Failed to load deliveries");
            }
        })();
        return () => { cancelled = true; };
    }, [user?.id]);

    const sortedRows = useMemo(() => {
        if (!rows) return [];
        return [...rows].sort((a, b) => {
            const ax = a.completed_at || a.updated_at || a.created_at || "";
            const bx = b.completed_at || b.updated_at || b.created_at || "";
            return bx.localeCompare(ax);
        });
    }, [rows]);

    const firstSeenIds = useMemo(() => loadSeenIds(user?.id), [user?.id]);

    if (rows === null) return <div className="p-12"><Loader label="Fetching deliveries" /></div>;

    return (
        <div className="p-8" data-testid="deliveries-page">
            {/* Header */}
            <div className="mb-8 border-b border-zinc-800 pb-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-400 mb-2">// Deliveries</div>
                <h1 className="font-heading font-black tracking-tighter text-4xl text-zinc-50">Your results</h1>
                <p className="text-sm text-zinc-400 mt-2 max-w-2xl leading-relaxed">
                    Every completed request lands here automatically. Click any delivery to open the dossier, dashboard, or report.
                </p>
            </div>

            {err && <div className="border border-red-900/60 bg-red-950/30 text-red-400 text-sm px-4 py-3 font-mono mb-6" data-testid="deliveries-error">{err}</div>}

            {/* Empty state */}
            {sortedRows.length === 0 && (
                <div className="border border-dashed border-zinc-800 p-12 text-center" data-testid="deliveries-empty">
                    <Package className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                    <div className="font-heading font-bold text-zinc-100 text-base">No deliveries yet</div>
                    <div className="text-sm text-zinc-500 leading-relaxed max-w-md mx-auto mt-1">
                        Your completed requests will appear here within seconds of being processed. Submit your first one to get started.
                    </div>
                    <Link
                        to="/app/requests"
                        data-testid="deliveries-cta-new"
                        className="inline-flex items-center gap-1.5 mt-4 font-mono text-xs uppercase tracking-wider text-amber-500 hover:text-amber-400 border-b border-amber-500/40 hover:border-amber-400 pb-0.5 transition-colors"
                    >
                        Submit a request <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                </div>
            )}

            {/* Deliveries list */}
            {sortedRows.length > 0 && (
                <div className="space-y-3" data-testid="deliveries-list">
                    {sortedRows.map((r) => (
                        <DeliveryCard key={r.id} r={r} fresh={!firstSeenIds.has(r.id)} />
                    ))}
                </div>
            )}
        </div>
    );
}

function DeliveryCard({ r, fresh }) {
    const meta = TYPE_META[r.type] || { label: r.type, icon: Inbox, accent: "text-zinc-400 border-zinc-700 bg-zinc-900/30" };
    const Icon = meta.icon;
    const firmName = r.payload?.firm_name || r.payload?.firm_a_name || r.payload?.target || "—";
    const target = r.payload?.firm_b_name || r.payload?.partner_name;

    // Detect if the result_link is an internal app URL we can render as a "View dossier" CTA.
    const link = r.result_link || "";
    const isPublic = /\/p\/[a-f0-9]/i.test(link);
    const isApp = link.startsWith("/app") || link.includes("/app/");
    const linkLabel = isPublic ? "Open public dossier" : isApp ? "Open in workspace" : "Open result";

    return (
        <article
            className={`border bg-zinc-950 ${fresh ? "border-emerald-500/40" : "border-zinc-800"} relative`}
            data-testid={`delivery-card-${r.id}`}
        >
            {fresh && (
                <span
                    className="absolute -top-1.5 -right-1.5 inline-flex items-center gap-1 bg-emerald-500 text-black font-mono font-bold text-[9px] uppercase tracking-wider px-1.5 py-0.5"
                    data-testid={`delivery-fresh-${r.id}`}
                >
                    <Zap className="w-2.5 h-2.5" /> New
                </span>
            )}
            <div className="p-5 flex items-start gap-4 flex-wrap">
                <div className={`w-11 h-11 border flex items-center justify-center shrink-0 ${meta.accent}`}>
                    <Icon className="w-5 h-5" />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className={`font-mono text-[10px] uppercase tracking-wider px-1.5 py-0.5 border ${meta.accent}`}>{meta.label}</span>
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                        <span className="font-mono text-[10px] uppercase tracking-wider text-emerald-400">Delivered</span>
                        <span className="font-mono text-[10px] text-zinc-600">· {fmtAgo(r.completed_at || r.updated_at)}</span>
                    </div>
                    <h2 className="font-heading font-bold text-lg text-zinc-50 mt-2 leading-snug">
                        {firmName}
                        {target && <span className="text-amber-500"> × {target}</span>}
                    </h2>
                    {r.result_notes && (
                        <p className="text-sm text-zinc-300 leading-relaxed mt-2 whitespace-pre-wrap" data-testid={`delivery-notes-${r.id}`}>
                            {r.result_notes}
                        </p>
                    )}
                    {r.notes && !r.result_notes && (
                        <p className="text-xs text-zinc-500 leading-relaxed mt-2 italic">
                            Your request: "{r.notes.slice(0, 200)}{r.notes.length > 200 ? "…" : ""}"
                        </p>
                    )}
                </div>
                {/* Right-side actions */}
                <div className="flex flex-col items-stretch gap-2 shrink-0 min-w-[160px]">
                    {link ? (
                        <a
                            href={link}
                            target={isPublic ? "_blank" : "_self"}
                            rel="noopener noreferrer"
                            data-testid={`delivery-open-${r.id}`}
                            className="inline-flex items-center justify-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-black font-bold text-[11px] uppercase tracking-wider px-3 py-2.5 transition-colors"
                        >
                            {isPublic ? <Eye className="w-3.5 h-3.5" /> : <FileText className="w-3.5 h-3.5" />}
                            {linkLabel}
                            <ExternalLink className="w-3 h-3 opacity-70" />
                        </a>
                    ) : (
                        <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 border border-zinc-800 px-3 py-2.5 text-center">
                            Notes-only
                        </span>
                    )}
                    <Link
                        to={`/app/requests?focus=${r.id}`}
                        data-testid={`delivery-view-request-${r.id}`}
                        className="inline-flex items-center justify-center gap-1 font-mono text-[10px] uppercase tracking-wider text-zinc-400 hover:text-amber-400 border border-zinc-800 hover:border-amber-500 px-3 py-1.5 transition-colors"
                    >
                        View request
                    </Link>
                </div>
            </div>
        </article>
    );
}
